/**
 * @hector/shared-types/billing — Zod schemas for the Stripe self-serve billing contract.
 *
 * Single source of truth for the `/api/stripe/*` request/response shapes
 * (plan 2026-08-12, docs/features/billing/plans/2026-08-12-stripe-billing-selfserve.html).
 * Used by:
 *   - apps/backend       : `.parse()` at the entry of every billing route.
 *   - apps/legalworkspace : `z.infer<...>` for the billing hooks/banner instead of
 *     locally-redefined interfaces.
 *
 * Business model (decided 2026-08-12, do not re-litigate here):
 *   - A cabinet usually has ONE Stripe subscription; partners paying separately
 *     give it several billing accounts (2026-10, migration 189) — never opened by
 *     the self-serve checkout, only posed by the Hector team.
 *   - Licences = purchased budget (`cabinet_subscriptions.quantity`), chosen at
 *     checkout. Self-serve can only INCREASE the budget; decrease/cancellation
 *     goes through support.
 *   - Stripe is the source of truth; the DB is a webhook-fed cache.
 */
import { z } from 'zod';

// ────────────────────────────────────────────────────────────────────────────
// 1. Plans
// ────────────────────────────────────────────────────────────────────────────

/**
 * The 3 self-serve plans. Each maps to one Stripe Price id, resolved
 * server-side from env (never hardcoded price ids in the apps):
 *   - `monthly`        → STRIPE_PRICE_ID_SEAT_MONTHLY
 *   - `annual_once`    → STRIPE_PRICE_ID_SEAT_ANNUAL_ONCE   (yearly interval)
 *   - `annual_monthly` → STRIPE_PRICE_ID_SEAT_ANNUAL_MONTHLY (monthly interval,
 *     12-month commitment enforced contractually, not by Stripe)
 */
export const BillingPlanSchema = z.enum(['monthly', 'annual_once', 'annual_monthly']);
export type BillingPlan = z.infer<typeof BillingPlanSchema>;

export * from './partners.js';
export * from './crm-read.js';
export * from './crm-prospect-read.js';

// ────────────────────────────────────────────────────────────────────────────
// 2. Requests
// ────────────────────────────────────────────────────────────────────────────

/** POST /api/stripe/create-checkout-session */
export const CreateCheckoutSessionRequestSchema = z.object({
  /** Licence budget purchased at checkout (Stripe line item quantity). */
  quantity: z.number().int().min(1).max(100),
  plan: BillingPlanSchema,
  /** Optional Stripe promotion code (e.g. `REF10-DUPONT`). Invalid → checkout proceeds without discount. */
  offer: z.string().max(64).optional(),
  /** Acquisition source tag (e.g. `referral`), persisted in subscription metadata for attribution. */
  ref: z.string().max(64).optional(),
});
export type CreateCheckoutSessionRequest = z.infer<typeof CreateCheckoutSessionRequestSchema>;

/**
 * Which billing account of the cabinet a mutation targets (`sub_…`, from
 * `BillingStatusResponse.accounts`). Optional: omitted, the server takes the
 * only account the caller manages and answers 400 when there are several.
 */
const TargetSubscriptionIdSchema = z.string().regex(/^sub_[A-Za-z0-9]+$/).max(255).optional();

/** POST /api/stripe/add-seats — increase-only by construction (min 1). */
export const AddSeatsRequestSchema = z.object({
  add: z.number().int().min(1).max(100),
  subscription_id: TargetSubscriptionIdSchema,
});
export type AddSeatsRequest = z.infer<typeof AddSeatsRequestSchema>;

/**
 * POST /api/stripe/switch-to-annual — only allowed FROM the monthly plan. The
 * change is SCHEDULED for the next renewal date (nothing is billed today).
 */
export const SwitchToAnnualRequestSchema = z.object({
  variant: z.enum(['once', 'monthly']),
  subscription_id: TargetSubscriptionIdSchema,
});
export type SwitchToAnnualRequest = z.infer<typeof SwitchToAnnualRequestSchema>;

/** POST /api/stripe/switch-to-annual response. */
export const SwitchToAnnualResponseSchema = z.object({
  success: z.literal(true),
  /** Date the annual plan starts (ISO) — the end of the period already paid. */
  effective_at: z.string().nullable(),
  /**
   * False in the general case (change scheduled for `effective_at`). True only
   * for a subscription still in its free trial: the plan is swapped right away,
   * nothing being billed before the end of the trial either way.
   */
  immediate: z.boolean(),
});
export type SwitchToAnnualResponse = z.infer<typeof SwitchToAnnualResponseSchema>;

/** POST /api/stripe/cancel-plan-change — drops the scheduled plan change, the subscription stays as it is. */
export const CancelPlanChangeRequestSchema = z.object({
  subscription_id: TargetSubscriptionIdSchema,
});
export type CancelPlanChangeRequest = z.infer<typeof CancelPlanChangeRequestSchema>;

/**
 * Postes ouverts par l'essai « cabinet » de 30 jours sans carte : 3 proposés par
 * défaut, 5 au maximum (décision Benoît, 2026-10-02 — auparavant 10 par défaut,
 * 100 au maximum). Un essai sans carte n'engage rien : le plafond borne ce qu'un
 * cabinet peut ouvrir gratuitement sans passer par l'équipe.
 *
 * Source unique : le sélecteur de la page d'inscription (legalworkspace), le
 * schéma ci-dessous et la garde serveur de `startCabinetTrial` (backend) en
 * dérivent tous — ne jamais réécrire ces nombres ailleurs.
 */
export const CABINET_TRIAL_MIN_SEATS = 1;
export const CABINET_TRIAL_DEFAULT_SEATS = 3;
export const CABINET_TRIAL_MAX_SEATS = 5;

/**
 * POST /api/stripe/start-trial — « grand cabinet » signup path: 30-day Stripe
 * trial without a card, opened from the `/inscription/cabinet` signup link. No
 * invite code (décision Benoît, 2026-10-05) : the server only enforces the seat
 * bounds and « one trial per cabinet that never subscribed ».
 */
export const StartTrialRequestSchema = z.object({
  quantity: z
    .number()
    .int()
    .min(CABINET_TRIAL_MIN_SEATS)
    // superRefine plutôt que .max() : le message doit citer la quantité reçue
    // ET la borne, pour être lisible hors contexte dans les logs du backend.
    .superRefine((value, ctx) => {
      if (value > CABINET_TRIAL_MAX_SEATS) {
        ctx.addIssue({
          code: 'custom',
          message: `a cabinet trial opens at most ${CABINET_TRIAL_MAX_SEATS} seats (CABINET_TRIAL_MAX_SEATS), got ${value}`,
        });
      }
    }),
  ref: z.string().max(64).optional(),
});
export type StartTrialRequest = z.infer<typeof StartTrialRequestSchema>;

/** POST /api/stripe/create-portal-session */
export const CreatePortalSessionRequestSchema = z.object({
  /** `payment_method_update` deep-links the Portal to the payment-method flow (past_due banner CTA). */
  flow: z.enum(['payment_method_update']).optional(),
  subscription_id: TargetSubscriptionIdSchema,
});
export type CreatePortalSessionRequest = z.infer<typeof CreatePortalSessionRequestSchema>;

// ────────────────────────────────────────────────────────────────────────────
// 3. Responses
// ────────────────────────────────────────────────────────────────────────────

/** Hosted-page redirect target (Checkout). The front does `window.location = url`. */
export const CheckoutSessionResponseSchema = z.object({
  url: z.string(),
});
export type CheckoutSessionResponse = z.infer<typeof CheckoutSessionResponseSchema>;

/** Hosted-page redirect target (Customer Portal). */
export const PortalSessionResponseSchema = z.object({
  url: z.string(),
});
export type PortalSessionResponse = z.infer<typeof PortalSessionResponseSchema>;

/** POST /api/stripe/start-trial response. */
export const StartTrialResponseSchema = z.object({
  /** End of the trial (ISO), as decided by Stripe. */
  trial_end: z.string().nullable(),
});
export type StartTrialResponse = z.infer<typeof StartTrialResponseSchema>;

/**
 * GET /api/stripe/pricing — the current PUBLIC price grid, read from Stripe.
 * The unit price decreases with the number of licences (volume tiers: the
 * price of the reached tier applies to every licence). Display only — Stripe
 * computes what is billed. `plans` is empty when Stripe is not configured.
 */
export const PricingTierSchema = z.object({
  /** Last quantity of the tier (inclusive); null = no upper bound. */
  up_to: z.number().int().nullable(),
  /** Price of ONE licence per interval, in the smallest currency unit (cents), excl. tax. */
  unit_amount: z.number().int(),
});
export const PricingGridResponseSchema = z.object({
  plans: z.array(
    z.object({
      plan: BillingPlanSchema,
      currency: z.string(),
      /** Stripe recurring interval: `month` or `year` for the self-serve plans. */
      interval: z.string(),
      interval_count: z.number().int(),
      tiers: z.array(PricingTierSchema),
    }),
  ),
});
export type PricingGridResponse = z.infer<typeof PricingGridResponseSchema>;

/**
 * A plan change scheduled for the next renewal date of a subscription (today:
 * monthly → annual, see `switch-to-annual`). Read from the webhook-fed cache.
 */
export const PendingPlanChangeSchema = z.object({
  /** Plan the subscription moves to; null when the target price is not one of the 3 self-serve plans. */
  plan: BillingPlanSchema.nullable(),
  /** Date the change takes effect (ISO). */
  effective_at: z.string(),
  /**
   * What Stripe will bill on that date, excl. tax, in the smallest currency
   * unit — taken from Stripe's own invoice preview, never computed by Hector.
   * Null when the preview could not be read, and for a caller who does not
   * manage the account (a plain member sees the date, not the amount).
   */
  next_amount: z.number().int().nullable(),
  currency: z.string().nullable(),
  /** Caller may cancel it from the app (manages the account AND the change was scheduled from the app). */
  can_cancel: z.boolean(),
});
export type PendingPlanChange = z.infer<typeof PendingPlanChangeSchema>;

/**
 * One billing account of a cabinet = one live Stripe subscription with its own
 * Stripe customer (card, invoices). Most cabinets have exactly one.
 */
export const BillingAccountSchema = z.object({
  /** Stripe subscription id — the handle to pass back as `subscription_id`. */
  subscription_id: z.string(),
  billing_status: z.string(),
  /** Licences paid by THIS account. */
  quantity: z.number().int(),
  plan: BillingPlanSchema.nullable(),
  current_period_end: z.string().nullable(),
  cancel_at_period_end: z.boolean(),
  /** Member designated to manage this account; null = the cabinet admins. */
  manager_email: z.string().nullable(),
  /** Caller may open the Portal / add seats on this account (admin, or its designated manager). */
  can_manage: z.boolean(),
  /** Plan change scheduled on THIS account; null/absent = none. */
  pending_plan_change: PendingPlanChangeSchema.nullable().optional(),
});
export type BillingAccount = z.infer<typeof BillingAccountSchema>;

/**
 * GET /api/stripe/status — DB-cache read only (zero Stripe API calls), so it
 * works even when no Stripe env is configured. Feeds both the BillingBanner
 * and the Settings → Facturation tab through a single hook.
 */
export const BillingStatusResponseSchema = z.object({
  /** `cabinet_subscriptions.status` (`trialing`/`active`/`past_due`/`canceled`…), `'none'` when no row. */
  billing_status: z.string(),
  /** Whether `cabinets.stripe_customer_id` is set (cabinet ↔ Stripe link established). */
  has_customer: z.boolean(),
  /** Licence budget purchased — sum of the live billing accounts (0 when no subscription row). */
  quantity: z.number().int(),
  /** COUNT(cabinet_members WHERE license_exempt = false). */
  seats_used: z.number().int(),
  /** Derived by matching `price_id` against the 3 env price ids; null for trial/manual/unknown rows. */
  plan: BillingPlanSchema.nullable(),
  current_period_end: z.string().nullable(),
  trial_end: z.string().nullable(),
  cancel_at_period_end: z.boolean(),
  /** Caller's `cabinet_role === 'admin'` — gates the mutation CTAs client-side. */
  is_admin: z.boolean(),
  /** Caller's `license_exempt` (staff) — shows the "no billing required" card. */
  is_exempt: z.boolean(),
  /**
   * The cabinet already has a REAL Stripe subscription (`sub_…`) that is not
   * terminal (`canceled` / `incomplete_expired`). Gates the UI: add seats and
   * portal when true, checkout only when false. Use this, never `plan`, to
   * decide whether a cabinet is subscribed — `plan` is null on negotiated prices.
   */
  has_live_subscription: z.boolean(),
  /**
   * Non-null when the billing of this space is managed by ANOTHER space of the
   * same firm (one payer, several spaces — #1835). `quantity`, `seats_used` and
   * the status are then those of the paying space, shared by the whole group;
   * the UI shows who to contact instead of any billing action. `email` is the
   * oldest admin of the paying space (null when it has none).
   */
  billing_manager: z.object({ email: z.string().nullable() }).nullable(),
  /**
   * Whether a cabinet may add licences itself. False (the default): an extra
   * licence is asked to the Hector team, who sets it in Stripe — the app follows
   * through the webhook. The UI then shows the support contact instead of a button.
   */
  self_serve_add_seats: z.boolean(),
  /**
   * The cabinet's live billing accounts, primary first. With several, the
   * top-level `quantity` is their sum and the top-level status describes the
   * account needing attention; the UI lists each one with its own actions.
   * Empty without a live subscription, and for a space whose billing is managed
   * elsewhere.
   */
  accounts: z.array(BillingAccountSchema),
  /**
   * Plan change scheduled on the subscription the top-level status describes;
   * null/absent = none. Always null for a space whose billing is managed
   * elsewhere (the payer's next payment is not this space's to see).
   */
  pending_plan_change: PendingPlanChangeSchema.nullable().optional(),
});
export type BillingStatusResponse = z.infer<typeof BillingStatusResponseSchema>;

/**
 * Un collaborateur d'un espace rattaché, vu par l'administrateur de l'espace qui
 * règle l'abonnement (Paramètres → Mes Collaborateurs, lecture seule). Strict
 * nécessaire : de quoi savoir QUI consomme une licence et depuis quel espace —
 * ni identifiant de compte, ni droits, ni avatar.
 */
export const ManagedSpaceMemberSchema = z.object({
  /** `cabinet_members.id` — clé de ligne, aucune action ne s'y rattache. */
  id: z.string(),
  full_name: z.string().nullable(),
  email: z.string().nullable(),
  /** Rôle dans SON espace, pas dans l'espace gestionnaire. */
  cabinet_role: z.enum(['admin', 'member']),
  /** `true` : ne consomme aucune licence (n'entre pas dans `seats_used`). */
  license_exempt: z.boolean(),
  cabinet_id: z.string(),
  cabinet_name: z.string(),
});
export type ManagedSpaceMember = z.infer<typeof ManagedSpaceMemberSchema>;

/**
 * GET /api/stripe/managed-space-members — administrateur de l'espace seulement.
 * Liste vide pour un espace sans espace rattaché, et pour un espace dont un
 * autre règle l'abonnement.
 */
export const ManagedSpaceMembersResponseSchema = z.object({
  members: z.array(ManagedSpaceMemberSchema),
});
export type ManagedSpaceMembersResponse = z.infer<typeof ManagedSpaceMembersResponseSchema>;

// ────────────────────────────────────────────────────────────────────────────
// 4. Billing overview (Settings → Facturation, read live from Stripe)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Default payment method of a billing account. Deliberately minimal: type,
 * brand and last 4 digits — never the expiry date, the holder or a fingerprint.
 */
export const BillingPaymentMethodSchema = z.object({
  /** Stripe payment method type (`card`, `sepa_debit`, …). */
  type: z.string(),
  /** Card brand as named by Stripe (`visa`, `mastercard`, …); null for non-card methods. */
  brand: z.string().nullable(),
  last4: z.string().nullable(),
});
export type BillingPaymentMethod = z.infer<typeof BillingPaymentMethodSchema>;

/** One invoice of the cabinet's own billing account. */
export const BillingInvoiceSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  /** Invoice date (ISO). */
  date: z.string(),
  /** Total incl. tax, in the smallest currency unit. */
  total: z.number().int(),
  currency: z.string(),
  /** Stripe invoice status: `paid`, `open`, `void`, `uncollectible`. */
  status: z.string(),
  /** Stripe-hosted invoice page; null when Stripe has none for this invoice. */
  hosted_invoice_url: z.string().nullable(),
});
export type BillingInvoice = z.infer<typeof BillingInvoiceSchema>;

/** GET /api/stripe/billing-overview query. */
export const BillingOverviewQuerySchema = z.object({
  subscription_id: TargetSubscriptionIdSchema,
});

/**
 * GET /api/stripe/billing-overview — payment method and invoices of ONE billing
 * account, read live from Stripe (so only fetched when the Facturation tab is
 * open). Same access rule as the Customer Portal: cabinet admins and the member
 * designated on the account; refused from a space whose billing is managed elsewhere.
 */
export const BillingOverviewResponseSchema = z.object({
  payment_method: BillingPaymentMethodSchema.nullable(),
  /** Most recent first, 12 at most, drafts excluded. */
  invoices: z.array(BillingInvoiceSchema),
  pending_plan_change: PendingPlanChangeSchema.nullable(),
});
export type BillingOverviewResponse = z.infer<typeof BillingOverviewResponseSchema>;
